import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

import { ITranslationSyncStatus } from "../../../@types/admin/AdminMasterTranslations";
import { TableSorted } from "../../../components/TableSorted";
import { useSyncTranslations } from "../../../hooks/mutations/admin/masterTranslations";
import {
  translationsSyncStatusKey,
  useTranslationsSyncStatus,
  useTranslationsSyncSummary,
} from "../../../hooks/queries/admin/masterTranslations";
import { useModal } from "../../../hooks/useModal";
import { getEnvironment, isProduction } from "../../../utils/environment";
import {
  AdminSelectField,
  ErrorMessage,
  FilterButton,
  FilterGroup,
  FilterLabel,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import {
  ActiveCellFilter,
  PageScopeBar,
  TranslationKey,
} from "./AdminTranslations.styles";
import { ConfirmSyncAllModal } from "./ConfirmSyncAllModal";
import { describeSyncResult, SyncKeyDetailModal } from "./SyncKeyDetailModal";
import {
  ISummaryCellFilter,
  keyStatusBadge,
  keyStatusText,
  matchesSummaryCell,
  summarizeKeyStatus,
  summaryColumnLabel,
} from "./syncState";
import { SyncSummaryTable } from "./SyncSummaryTable";

const SEARCH_FIELDS: (keyof ITranslationSyncStatus)[] = ["key"];

type StatusFilter = "" | "needs-sync" | "in-sync" | "only-local";

/**
 * Translations Sync page (EN-5178), the pull-based replacement for the Retool
 * migrate_GlobalTranslations app.
 *
 * Retool pushed from the master store into all four Global DBs from one page.
 * The portal's auth model binds a session to ONE environment, so the flow is
 * inverted: every environment has this page, and it only pulls from the master
 * store into the environment you are logged into. To publish a key everywhere,
 * sync it once per environment, in that environment's portal.
 *
 * Above the key list, a per-language summary (EN-5330) counts what is in sync,
 * different, missing on either side, missing vs English and archived; clicking
 * a count filters the key list to the keys behind it.
 */
export function AdminTranslationsSync() {
  document.title = "Translations sync - Admin";

  const { openModal, closeModal } = useModal();
  const environmentName = getEnvironment();

  const { status, isLoading, isError, isNotConfigured } =
    useTranslationsSyncStatus();
  const { syncTranslations, isSyncing } = useSyncTranslations();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [usedLocationFilter, setUsedLocationFilter] = useState("");
  const [cellFilter, setCellFilter] = useState<ISummaryCellFilter | null>(null);

  const {
    summary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = useTranslationsSyncSummary(usedLocationFilter || undefined);

  const usedLocations = useMemo(
    () => Array.from(new Set(status.map((s) => s.usedLocation))).sort(),
    [status]
  );

  const rows = useMemo(
    () =>
      status.filter((s) => {
        if (usedLocationFilter && s.usedLocation !== usedLocationFilter) {
          return false;
        }
        // A summary cell and the status select are alternatives: picking one
        // clears the other, so at most one of them is set here.
        if (cellFilter) return matchesSummaryCell(s, cellFilter);
        if (!statusFilter) return true;
        const { needsSync, onlyInLocal } = summarizeKeyStatus(s);
        switch (statusFilter) {
          case "needs-sync":
            return needsSync;
          case "only-local":
            return onlyInLocal > 0;
          case "in-sync":
          default:
            return !needsSync && onlyInLocal === 0;
        }
      }),
    [status, statusFilter, usedLocationFilter, cellFilter]
  );

  const selectCell = useCallback((filter: ISummaryCellFilter) => {
    setStatusFilter("");
    setCellFilter(filter);
  }, []);

  const pendingKeys = useMemo(
    () => status.filter((s) => summarizeKeyStatus(s).needsSync),
    [status]
  );

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({ data: rows, searchFields: SEARCH_FIELDS });

  const openKeyDetail = (row: ITranslationSyncStatus) => {
    openModal(
      <SyncKeyDetailModal
        translationKey={row.key}
        usedLocation={row.usedLocation}
        environmentName={environmentName}
        onClose={closeModal}
      />,
      { width: "900px", showCloseButton: true }
    );
  };

  const handleSyncAll = () => {
    if (pendingKeys.length === 0) return;
    openModal(
      <ConfirmSyncAllModal
        keyCount={pendingKeys.length}
        environmentName={environmentName}
        isProductionEnvironment={isProduction()}
        onConfirm={async () => {
          const result = await syncTranslations({
            items: pendingKeys.map((s) => ({
              key: s.key,
              usedLocation: s.usedLocation,
            })),
          });
          return describeSyncResult(result);
        }}
        onClose={closeModal}
      />,
      { width: "520px", showCloseButton: true }
    );
  };

  const columns: ColumnDef<ITranslationSyncStatus, string>[] = useMemo(
    () => [
      {
        accessorKey: "key",
        header: "Key",
        cell: ({ row }) => (
          <TranslationKey title={row.original.key}>
            {row.original.key}
          </TranslationKey>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "usedLocation",
        header: "Used location",
        enableSorting: true,
      },
      {
        id: "languages",
        header: "Languages",
        // Master languages only; local-only ones show in the status text.
        accessorFn: (s) => String(summarizeKeyStatus(s).total),
        enableSorting: false,
      },
      {
        id: "status",
        header: `Status in ${environmentName}`,
        accessorFn: (s) => keyStatusText(summarizeKeyStatus(s)),
        cell: ({ row }) => {
          const summary = summarizeKeyStatus(row.original);
          return (
            <StatusBadge status={keyStatusBadge(summary)}>
              {keyStatusText(summary)}
            </StatusBadge>
          );
        },
        enableSorting: true,
      },
    ],
    [environmentName]
  );

  if (isNotConfigured) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>Translations sync</PageTitle>
        </PageHeader>
        <ErrorMessage>
          The master translations database is not configured in this
          environment, so there is nothing to sync from. Ask the team to add the
          TranslationsMaster connection string to this environment&apos;s API.
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>Translations sync</PageTitle>
          <PageInfoTooltip title="Translations sync">
            Pulls translations from the master store into the {environmentName}{" "}
            environment — this page can never write anywhere else. To publish a
            key to another environment, open this page in that
            environment&apos;s portal. Click a row to compare its values and
            sync per language. The summary counts each language per key and used
            location, master against {environmentName}; a row archived on either
            side is left out of those counts and shown in the Archived in master
            / Archived in {environmentName} columns. Missing vs English (master)
            compares master with itself: keys with an unarchived en-GB row in
            master and no row in that language in master. Click a count to list
            the keys behind it.
          </PageInfoTooltip>
        </PageTitleGroup>
      </PageHeader>

      {isSummaryError && (
        <ErrorMessage>
          Failed to load the per-language summary. Try refreshing the page.
        </ErrorMessage>
      )}

      {/* Page scope: the used location drives both tables, and "Sync all
          pending" ignores every filter — so neither sits with the list's own
          filters below. */}
      <PageScopeBar>
        <FilterGroup>
          <FilterLabel htmlFor="sync-used-location-filter">
            Used location
          </FilterLabel>
          <AdminSelectField
            id="sync-used-location-filter"
            value={usedLocationFilter}
            onChange={(e) => setUsedLocationFilter(e.target.value)}
          >
            <option value="">All locations</option>
            {usedLocations.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterButton
          variant="primary"
          disabled={isSyncing || pendingKeys.length === 0}
          onClick={handleSyncAll}
        >
          {isSyncing
            ? "Syncing..."
            : `Sync all pending (${pendingKeys.length})`}
        </FilterButton>
      </PageScopeBar>

      <TableContainer>
        <SyncSummaryTable
          summary={summary}
          isLoading={isSummaryLoading}
          environmentName={environmentName}
          selected={cellFilter}
          onSelect={selectCell}
        />
      </TableContainer>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by key..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <FilterGroup>
          <FilterLabel htmlFor="sync-status-filter">Status</FilterLabel>
          <AdminSelectField
            id="sync-status-filter"
            value={statusFilter}
            onChange={(e) => {
              setCellFilter(null);
              setStatusFilter(e.target.value as StatusFilter);
            }}
          >
            <option value="">All keys</option>
            <option value="needs-sync">Needs sync</option>
            <option value="in-sync">In sync</option>
            <option value="only-local">Only in {environmentName}</option>
          </AdminSelectField>
        </FilterGroup>
      </FiltersContainer>

      {cellFilter && (
        <ActiveCellFilter>
          <span>
            Showing keys behind{" "}
            <strong>
              {cellFilter.languageCode} ·{" "}
              {summaryColumnLabel(cellFilter.column, environmentName)}
            </strong>
          </span>
          <FilterButton variant="secondary" onClick={() => setCellFilter(null)}>
            Clear
          </FilterButton>
        </ActiveCellFilter>
      )}

      {isError && (
        <ErrorMessage>
          Failed to load the sync status. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[translationsSyncStatusKey]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No keys found"}
            onRowClick={openKeyDetail}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: `translations-sync-${environmentName}`,
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
